import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";

/**
 * HomeMobile — the portrait-only landing.
 *
 * The W1 image now fills the entire viewport (cover, centered) instead of
 * being letterboxed inside a centered square. The five curved nav labels are
 * laid out around the central fiddle leaf as it appears in this portrait
 * crop, with PANDURATA crowning the very top.
 *
 * Coordinate system: viewBox 0 0 1000 2200 (aspect ≈ 0.45, close to a modern
 * phone's 19.5:9). preserveAspectRatio="xMidYMid slice" keeps the SVG fully
 * filling the screen — small phones may crop a few pixels at top/bottom but
 * the central composition stays intact.
 */

// --- Curve paths (1000×2200 viewBox) -----------------------------------
// Each curve traces the actual organic edge it sits next to — the leaf's
// left and right contours, the small leaf-mass above, the U-shaped base.
// Inspired directly from the artist's mockup.
//
// Per the artist's direction, on mobile we SWAP the curves used for
// ART DEPT. and MANIFESTO — Art Dept. takes the longer left contour and
// Manifesto sits on the top-left dome. Desktop is untouched (see Home.jsx).

// MANIFESTO — diagonal curve sloping down-left from above the leaf, hugging
// the underside of the small left-side leaf-mass (was ART DEPT.).
// Nudged down ~50 units for legibility (sits below the wordmark with more room).
const PATH_M_MANIFESTO = "M 100 730 C 180 670, 280 610, 420 570";

// ARCHITECTURE — follows the leaf's right contour: a ")" shape that bulges
// outward (right) and returns at the leaf's right shoulder + right cheek.
const PATH_M_ARCHITECTURE = "M 650 640 C 900 820, 900 1200, 650 1380";

// ART DEPT. — mirror "(" shape along the leaf's left contour, bulging
// outward to the left at the widest point of the left lobe (was MANIFESTO).
const PATH_M_ARTDEPT = "M 340 620 C 90 800, 90 1180, 340 1360";

// REPOSITORY — gentle U dip following the underside of the leaf's base.
const PATH_M_REPOSITORY = "M 200 1660 Q 500 1740 800 1660";

// LET'S TALK — bottom-right diagonal climbing along the right vine.
const PATH_M_LETSTALK = "M 460 1900 Q 690 1820 920 1720";

const HomeMobile = () => {
  const navigateTo = useNavigate();
  const navigate = (path) => navigateTo(path);

  const onKey = (e, path) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <main className="home home--mobile" data-testid="home-page">
      <div className="home__scene" data-testid="home-scene">
        <h1
          className="home__wordmark home__wordmark--mobile"
          data-testid="wordmark-pandurata"
        >
          PANDURATA
        </h1>

        <svg
          className="home__svg home__svg--mobile"
          viewBox="0 0 1000 2200"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Pandurata navigation"
        >
          <defs>
            <path id="m-curve-artdept" d={PATH_M_ARTDEPT} />
            <path id="m-curve-architecture" d={PATH_M_ARCHITECTURE} />
            <path id="m-curve-manifesto" d={PATH_M_MANIFESTO} />
            <path id="m-curve-repository" d={PATH_M_REPOSITORY} />
            <path id="m-curve-letstalk" d={PATH_M_LETSTALK} />
          </defs>

          {/* ART DEPT — top-left dome */}
          <text
            className="home__label home__label--mobile"
            role="link"
            tabIndex={0}
            aria-label="Art Dept"
            onClick={() => navigate("/art-dept")}
            onKeyDown={(e) => onKey(e, "/art-dept")}
            data-testid="label-artdept"
          >
            <textPath href="#m-curve-artdept" startOffset="50%" textAnchor="middle">
              ART DEPT.
            </textPath>
          </text>

          {/* ARCHITECTURE — right-side vertical curve */}
          <text
            className="home__label home__label--mobile"
            role="link"
            tabIndex={0}
            aria-label="Architecture"
            onClick={() => navigate("/architecture")}
            onKeyDown={(e) => onKey(e, "/architecture")}
            data-testid="label-architecture"
          >
            <textPath href="#m-curve-architecture" startOffset="50%" textAnchor="middle">
              ARCHITECTURE
            </textPath>
          </text>

          {/* MANIFESTO — left-side vertical curve */}
          <text
            className="home__label home__label--mobile"
            role="link"
            tabIndex={0}
            aria-label="Manifesto"
            onClick={() => navigate("/manifesto")}
            onKeyDown={(e) => onKey(e, "/manifesto")}
            data-testid="label-manifesto"
          >
            <textPath href="#m-curve-manifesto" startOffset="50%" textAnchor="middle">
              MANIFESTO
            </textPath>
          </text>

          {/* REPOSITORY — base of leaf, shallow upward arc */}
          <text
            className="home__label home__label--mobile"
            role="link"
            tabIndex={0}
            aria-label="Repository"
            onClick={() => navigate("/repository")}
            onKeyDown={(e) => onKey(e, "/repository")}
            data-testid="label-repository"
          >
            <textPath href="#m-curve-repository" startOffset="50%" textAnchor="middle">
              REPOSITORY
            </textPath>
          </text>

          {/* LET'S TALK — bottom-right vine, climbing diagonal */}
          <text
            className="home__label home__label--mobile"
            role="link"
            tabIndex={0}
            aria-label="Let's Talk"
            onClick={() => navigate("/lets-talk")}
            onKeyDown={(e) => onKey(e, "/lets-talk")}
            data-testid="label-letstalk"
          >
            <textPath href="#m-curve-letstalk" startOffset="50%" textAnchor="middle">
              LET&apos;S TALK
            </textPath>
          </text>
        </svg>
      </div>

      <Link
        to="/ai"
        className="ai-toggle"
        aria-label="Switch to AI-readable mode"
        data-testid="ai-toggle"
      >
        <Bot size={13} strokeWidth={1.7} />
        <span>AI READABLE</span>
      </Link>
    </main>
  );
};

export default HomeMobile;
